import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  CalendarOff,
  CheckCircle2,
  FileText,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useMeals, usePatientReports } from '@/hooks/queries';

/**
 * "Requiring Action" indicator for the global header (beside the Invitation
 * code button). A FIRST-PASS triage surface (draft reports, missing-meal days,
 * stale logs) that reads its state at a glance:
 *   - no actions  → a static green "No actions" pill (nothing to expand);
 *   - ≥1 action   → an amber count button that opens a popover with the list.
 *
 * Self-sufficient: takes only `patientId` and fetches what it needs. Extend by
 * adding to the `useActionItems` list.
 */
type Props = { patientId: number };

type ActionItem = {
  id: string;
  icon: LucideIcon;
  title: string;
  detail?: string;
  cta?: { label: string; href: string };
};

const useActionItems = (patientId: number): { items: ActionItem[]; isLoading: boolean } => {
  const { t } = useTranslation();
  const reports = usePatientReports(patientId);

  // The "missing meal days" + "stale last meal" checks read TODAY's reality,
  // not a page-level date range — picking a range from last year shouldn't make
  // this claim "no meals in the last 7 days". A fixed 30-day window from today.
  // TanStack Query dedupes by key, so this is free when another consumer already
  // requested the same range.
  const last30dRange = useMemo(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
  }, []);
  const mealsLast30d = useMeals(patientId, last30dRange);

  const items = useMemo<ActionItem[]>(() => {
    const out: ActionItem[] = [];
    const drafts = (reports.data ?? []).filter((r) => r.status === 'draft');
    for (const r of drafts.slice(0, 3)) {
      out.push({
        id: `draft-${r.patient_report_id}`,
        icon: FileText,
        title: t('Draft report awaiting review'),
        detail: `${r.date_since} → ${r.date_until}`,
        cta: { label: t('Review'), href: `/patients/${patientId}/reports` },
      });
    }
    if ((mealsLast30d.data ?? []).length > 0) {
      const last7 = new Date();
      last7.setDate(last7.getDate() - 7);
      const cutoff = last7.toISOString().slice(0, 10);
      const mealDays = new Set(
        (mealsLast30d.data ?? [])
          .map((m) => m.meal_date)
          .filter((d): d is string => !!d && d >= cutoff),
      );
      const missing = 7 - mealDays.size;
      if (missing >= 3) {
        out.push({
          id: 'missing-meals',
          icon: CalendarOff,
          title: t('{{n}} of the last 7 days have no meal logs', { n: missing }),
          cta: { label: t('View logs'), href: `/patients/${patientId}/logs-v3` },
        });
      }
    }
    const lastMeal = (mealsLast30d.data ?? [])
      .map((m) => m.meal_date)
      .filter((d): d is string => !!d)
      .sort((a, b) => b.localeCompare(a))[0];
    if (lastMeal) {
      const lastDate = new Date(`${lastMeal}T00:00:00`);
      const ageDays = Math.floor((Date.now() - lastDate.getTime()) / 86_400_000);
      if (ageDays > 3) {
        out.push({
          id: 'stale-meals',
          icon: AlertCircle,
          title: t('Last meal logged {{n}} days ago', { n: ageDays }),
          detail: lastMeal,
        });
      }
    }
    return out;
  }, [reports.data, mealsLast30d.data, patientId, t]);

  return { items, isLoading: reports.isLoading || mealsLast30d.isLoading };
};

export const RequiringActionIndicator = ({ patientId }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { items, isLoading } = useActionItems(patientId);

  // Render nothing until the data resolves, so the indicator doesn't flash
  // "No actions" and then flip to a count.
  if (isLoading) return null;

  // No actions → a static "all clear" pill; nothing to expand.
  if (items.length === 0) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700"
        title={t('Nothing pending for this patient right now.')}
      >
        <CheckCircle2 className="h-4 w-4" />
        {t('No actions')}
      </span>
    );
  }

  // ≥1 action → an amber count that opens the list.
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 hover:text-amber-900"
        >
          <AlertCircle className="mr-2 h-4 w-4 text-amber-600" />
          {items.length}{' '}
          {items.length === 1 ? t('action needed') : t('actions needed')}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-80 p-1.5">
        <div className="px-2 py-1 text-[0.6875rem] font-semibold uppercase tracking-wide text-muted-foreground">
          {t('Requiring Action')}
        </div>
        <ul className="divide-y">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.id} className="flex items-start gap-2 px-2 py-2 text-xs">
                <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium leading-tight">{item.title}</div>
                  {item.detail && (
                    <div className="text-[0.6875rem] text-muted-foreground">{item.detail}</div>
                  )}
                  {item.cta && (
                    <Link
                      to={item.cta.href}
                      onClick={() => setOpen(false)}
                      className="mt-0.5 inline-block text-[0.6875rem] text-primary hover:underline"
                    >
                      {item.cta.label} →
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
